import { Component, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { HomeCheckrideRepositoryService } from '../../../shared/services/home-checkride-repository.service';
import { AppMetaData, CheckRideRequestSubStatusAllItems } from '../../../../meta-data/app-meta-data.service';
import { ApplicantDashboardComponent } from '../applicant-dashboard/applicant-dashboard.component';
import { ApplicantRepositoryService } from '../../services/applicant-repository.service';
import { buildCheckrideCard, CheckrideCard } from '../../../shared/models/checkride-card.model';

@Component({
  selector: 'app-applicant-home',
  templateUrl: './applicant-home.component.html',
  styleUrls: ['../../../shared/components/home-dashboard/home-dashboard.component.scss', './applicant-home.component.scss']
})
export class ApplicantHomeComponent extends ApplicantDashboardComponent implements OnDestroy {
  currentCheckride: CheckrideCard;
  pastCheckrides: CheckrideCard[] = [];
  hasCheckrides = false;
  errorMessage: string;
  private destroy$ = new Subject<void>();

  constructor(private homeRepository: ApplicantRepositoryService,
    private homeCheckrideRepository: HomeCheckrideRepositoryService) {
    super(homeRepository);
  }

  get attentionTaskCount(): number {
    return this.tasks.filter(task => task.taskTitle !== 'No current tasks').length;
  }

  loadStats(): void {
    this.loading = true;
    this.errorMessage = null;
    this.homeRepository.getCurrentApplicantinfo(false, true).pipe(
      mergeMap(applicant => this.homeCheckrideRepository.load('applicant', applicant?.StudentApplicantId)
        .pipe(map(checkrides => ({ applicant, checkrides })))),
      takeUntil(this.destroy$)
    ).subscribe(({ applicant, checkrides }) => {
      if (!applicant) {
        this.errorMessage = 'Your applicant profile could not be loaded.';
        this.loading = false;
        return;
      }
      // Existing task rules consume the result relationship; the home query returns its selected fields flat.
      this.applicant = { ...applicant, CheckRideRequest: checkrides.map(checkride => ({
        ...checkride,
        CheckRideResult: {
          ...AppMetaData.CheckRideResult.CreateEntity(),
          CheckRideOutcomeId: checkride.CheckRideResultCheckRideOutcomeId,
          AllowRetest: checkride.CheckRideResultAllowRetest
        }
      })) };
      this.applicantName = applicant.DwUser?.FirstName || '';
      this.buildTasks(this.applicant);
      const cards = checkrides.map(checkride => buildCheckrideCard(checkride, 'applicant'));
      this.hasCheckrides = cards.length > 0;
      const closed = [CheckRideRequestSubStatusAllItems.Completed, CheckRideRequestSubStatusAllItems.Cancelled,
        CheckRideRequestSubStatusAllItems.Rejected, CheckRideRequestSubStatusAllItems.Deleted];
      const active = cards.filter(card => !closed.includes(card.entity.CheckRideRequestSubStatusId));
      active.sort((a, b) => (a.scheduledMoment?.valueOf() || Number.MAX_SAFE_INTEGER)
        - (b.scheduledMoment?.valueOf() || Number.MAX_SAFE_INTEGER));
      this.currentCheckride = active[0] || null;
      this.pastCheckrides = cards.filter(card => card !== this.currentCheckride
        && (closed.includes(card.entity.CheckRideRequestSubStatusId) || card.scheduledMoment?.isBefore(undefined, 'day')))
        .sort((a, b) => (b.scheduledMoment?.valueOf() || 0) - (a.scheduledMoment?.valueOf() || 0))
        .slice(0, 10);
      this.loading = false;
    }, () => {
      this.errorMessage = 'Your dashboard could not be loaded. Please try again.';
      this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
